import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { createUser, CreateUserData } from '../../services/userService';
import { useAuth } from '../../hooks/useAuth';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

/**
 * Dialog for creating a new user
 */
const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateUserData>({
    school_id: user?.attributes?.['custom:school_id'] || '',
    role: 'student',
    name: '',
    email: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    password: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    setFormErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleRoleChange = (event: any) => {
    setFormData((prev) => ({
      ...prev,
      role: event.target.value,
    }));
  };

  const validateForm = (): boolean => {
    const errors = {
      name: '',
      email: '',
      password: '',
    };
    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
      isValid = false;
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
      isValid = false;
    }

    // Validate password (optional, but if provided must meet requirements)
    if (formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const dataToSend: CreateUserData = {
        school_id: formData.school_id,
        role: formData.role,
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
      };

      // Only include password if provided
      if (formData.password) {
        dataToSend.password = formData.password;
      }

      const response = await createUser(dataToSend);

      // If a temporary password was generated, show it
      if (response.temporaryPassword) {
        setTemporaryPassword(response.temporaryPassword);
      } else {
        // If no temporary password, close dialog and show success
        onSuccess(response.message);
        handleClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      school_id: user?.attributes?.['custom:school_id'] || '',
      role: 'student',
      name: '',
      email: '',
      password: '',
    });
    setFormErrors({
      name: '',
      email: '',
      password: '',
    });
    setError(null);
    setTemporaryPassword(null);
    setShowPassword(false);
    onClose();
  };

  const handleCopyPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
    }
  };

  const handlePasswordDialogClose = () => {
    onSuccess('User created successfully');
    handleClose();
  };

  // If showing temporary password
  if (temporaryPassword) {
    return (
      <Dialog open={open} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          User Created Successfully
          <IconButton
            onClick={handlePasswordDialogClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Please save this temporary password. It will not be shown again.
          </Alert>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '1.2rem',
              textAlign: 'center',
              wordBreak: 'break-all',
            }}
          >
            {temporaryPassword}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The user will be required to change this password on first login.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyPassword}>Copy Password</Button>
          <Button onClick={handlePasswordDialogClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add New User
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            error={!!formErrors.name}
            helperText={formErrors.name}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={loading}
          />

          <FormControl fullWidth required>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={handleRoleChange}
              disabled={loading}
            >
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
              <MenuItem value="school_admin">School Admin</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Password (Optional)"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            error={!!formErrors.password}
            helperText={formErrors.password || 'Leave blank to auto-generate a temporary password'}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
